import { z } from 'zod';
import { RequestStatusSchema } from './requests';
import { PlayOptionSchema, PlayUiCapabilitiesSchema } from './play-catalog';

/**
 * Flow Queuing System (v0) wire schemas — the DB row shapes for
 * `enrollment_item`/`enrollment_batch` (mirroring the Prisma models in
 * prisma/schema.prisma), plus the request/response contracts for
 * `/api/flow/queue`.
 *
 * Schemas are the source of truth (z.infer only, no parallel hand-written
 * types — `terra/no-parallel-schema-and-type`).
 */

// ── Lifecycle ─────────────────────────────────────────────────────────────

/**
 * Pre-send lifecycle of an enrollment_item (D1). Delivery state is never
 * mirrored here — read it off ActionExecution via correlation_id/
 * execution_id once submitted.
 */
export const EnrollmentItemStatusSchema = z.enum(['unassigned', 'draft', 'ready', 'queued', 'sending', 'submitted']);
export type EnrollmentItemStatus = z.infer<typeof EnrollmentItemStatusSchema>;

/**
 * Who authored a step draft's copy (Gong Flow Play + Queue redesign, U11) —
 * 'rep' for a rep-typed override (the PUT route hardcodes this; a rep never
 * supplies it directly), 'generated' for the copy-generation fork's
 * producer-written draft. Read-side-string/write-side-enum convention (same
 * as `preferred_channel` below, and `EnrollmentItemStep.copySource`'s Prisma
 * doc comment): the DB column and `StepDraftSchema.copy_source` stay a plain
 * string so a hand-edited or schema-predating row still round-trips; this
 * strict enum exists only to type `upsertStepDraft`'s `copySource` parameter
 * (`z.infer`, not a hand-written parallel union — `terra/no-parallel-schema-and-type`).
 */
export const StepDraftCopySourceSchema = z.enum(['rep', 'generated']);
export type StepDraftCopySource = z.infer<typeof StepDraftCopySourceSchema>;

/**
 * The copy fields a send-attempt snapshot can carry for ONE step (DBOS
 * durable-send migration, F1 U1) — the single property vocabulary
 * `enrollment_send_attempt.copy_snapshot` uses, shared by U2 (`base`), U3a
 * (`finalizeItem`'s `sent`), and U5 (`buildGongFlowCopySnapshot`). Every
 * writer of `copySnapshot` validates through `SendCopySnapshotSchema` below
 * before persisting — never `.parse()` directly on a read
 * (`terra/no-unsafe-schema-parse`); reads go through
 * `parseDbJson(SendCopySnapshotSchema, ...)`.
 *
 * Null-vs-omission policy: OMIT a field the play type never forwards (e.g.
 * per-step `sender` for `gong_flow`); use `null` for a field the play type
 * DOES forward but that had no value at send time.
 */
export const SendCopyFieldsSchema = z.object({
  sender: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
}).strict();
export type SendCopyFields = z.infer<typeof SendCopyFieldsSchema>;

/**
 * The server-resolved sender/subject/body for one admitted item — i.e. WHAT
 * ACTUALLY SENDS — carried in `FlowSendWorkflowArgs.resolvedContent` (DBOS
 * durable-send hardening, H-U3 / D6). `ResolvedSendContent.sent` in
 * `send-content-resolution.ts` is derived from this schema so the two cannot
 * drift; the name is `Sent`, not `Send`, to stay distinct from that
 * module's `{ sent, base }` pair.
 *
 * WHY THIS TRAVELS IN THE WORKFLOW ARGS instead of being written onto the row
 * at claim time: `admitQueueSend` used to persist the resolved trio onto
 * `enrollment_items.sender/subject_draft/body_draft`, and nothing restored it
 * on cancel, on a failure finalize, or on enqueue compensation. Because
 * `send-content-resolution.ts` reads a non-null `subjectDraft`/`bodyDraft` as
 * "the rep overrode this", a persisted staged value permanently converted a
 * base-following row into a manual override — silently, on any of those three
 * exit paths. Carrying it as checkpointed workflow INPUT gives one source of
 * truth with nothing to revert (and matches Decision 8: everything the
 * workflow needs is in its args).
 *
 * The 20 KB per-field cap is a sanity bound, not a product limit — the editor
 * imposes no cap, so a value this large means something is wrong upstream, and
 * a Zod rejection at `SendSchema` time is preferable to silent truncation.
 */
export const ResolvedSentContentSchema = z
  .object({
    sender: z.string().max(20_000).nullable(),
    subjectDraft: z.string().max(20_000).nullable(),
    bodyDraft: z.string().max(20_000).nullable(),
  })
  .strict();
export type ResolvedSentContent = z.infer<typeof ResolvedSentContentSchema>;

/**
 * One `enrollment_send_attempt.copy_snapshot` entry per step of the play the
 * attempt is dispatching (DBOS durable-send migration, F1 U1, Decision 15).
 * Single-step plays (event_invite, warm_intro) write exactly one entry
 * (`stepNumber: 0`); `gong_flow` writes one per flow step. `base` is the
 * copy resolved before send (rep draft or generated default); `sent` is what
 * actually went out. `base` deep-equal `sent` on an entry IS the
 * "not-overridden" signal — there is no separate touched/copySource flag on
 * this table (see `EnrollmentItemStep.copySource` for the pre-send editor's
 * own, unrelated, override flag).
 */
export const SendCopySnapshotSchema = z.array(
  z.object({
    stepNumber: z.number().int(),
    stepType: z.string(),
    base: SendCopyFieldsSchema,
    sent: SendCopyFieldsSchema,
  }).strict(),
);
export type SendCopySnapshot = z.infer<typeof SendCopySnapshotSchema>;
/** One entry of a `SendCopySnapshot` — the shape `EnrollmentSenderResult.copySnapshotSteps`
 * (`enrollment-sender.ts`) and `DispatchOutcome.copySnapshotSteps` (`flow-send-steps.ts`,
 * DBOS durable-send migration F1 U3) carry, before `finalizeItem` assembles the full array. */
export type SendCopySnapshotEntry = SendCopySnapshot[number];

// ── DB row schemas ────────────────────────────────────────────────────────

/**
 * One enrollment_item_step row (Gong Flow Play + Queue redesign, U11) — a
 * sparse per-step draft override for a `gong_flow` play, joined onto its
 * parent item's `step_drafts` at read time (see `EnrollmentItemSchema` below)
 * and also returned standalone by `listStepDrafts`/`upsertStepDraft`
 * (`services/flow/step-drafts.ts`). An item with no overridden steps simply
 * has no rows here — there is no "empty draft" row.
 */
export const StepDraftSchema = z.object({
  enrollment_item_id: z.number(),
  step_number: z.number(),
  step_type: z.string(),
  subject_draft: z.string().nullable(),
  body_draft: z.string().nullable(),
  // Plain string on the READ side (see `StepDraftCopySourceSchema`'s doc
  // comment above) — never re-validated against the enum here.
  copy_source: z.string(),
  updated_at: z.string(),
}).strict();
export type StepDraft = z.infer<typeof StepDraftSchema>;

/**
 * DELETE /api/flow/queue/items/:id/steps/:n success body — a small ack, sent
 * even when there was nothing to clear (the delete is idempotent).
 */
export const StepDraftClearResponseSchema = z.object({ cleared: z.literal(true) });
export type StepDraftClearResponse = z.infer<typeof StepDraftClearResponseSchema>;

/**
 * One enrollment_items row (D2: the queue's single draft store — the
 * sidebar editor and the full-screen review read/write the same row).
 * `correlation_id`/`execution_id` are soft refs set on submit, joined
 * against `ActionExecution` for delivery state (D1 — never mirrored here).
 */
export const EnrollmentItemSchema = z.object({
  id: z.number(),
  batch_id: z.number(),
  owner_user_id: z.number(),
  contact_id: z.string(),
  // Nullable since Warm Intro plan U9 (R4): ~55% of warm-intro targets are
  // LinkedIn-sourced with no email at all. `addQueueItem`/`patchQueueItem`
  // only ever persist null for a play whose connector marks email optional
  // (derived from inputSchema — play-capabilities.ts; today that's only
  // `warm_intro`) — every email-requiring play still refuses a no-email
  // contact, so a null here means "a no-email-capable play, LinkedIn-sourced".
  contact_email: z.string().transform(v => v.toLowerCase()).nullable(),
  /**
   * The contact's `salesforce_contact.linkedin`, joined in at read time (U9)
   * — NOT a stored `enrollment_items` column. It's what the queue renders in
   * place of the email for a no-email contact, so it must travel with every
   * item the rep sees. Null when the contact has no LinkedIn, or on the
   * internal send-claim path whose response is ids-only (see
   * `admitQueueSend`).
   */
  contact_linkedin: z.string().nullable(),
  /**
   * The contact's `salesforce_contact.name`, joined in at read time (U1 —
   * Gong Flow Play + Queue redesign) — NOT a stored `enrollment_items`
   * column, same joined-at-read convention as `contact_linkedin` (same
   * tombstone filter — see `loadContactDisplay` in `queue.ts`). Null when
   * the contact has no name on file, is absent from the mirror, or is
   * tombstoned. The queue row falls back to the existing
   * email/linkedin/contact-id identity chain when this is null
   * (`contactDisplayIdentity`) — never a fabricated "Unknown" label.
   */
  contact_name: z.string().nullable(),
  /**
   * The contact's `salesforce_contact.title`, joined in at read time (D7 —
   * flow-contacts-bulk-send design-fidelity plan, item 7) — NOT a stored
   * `enrollment_items` column, same joined-at-read convention as
   * `contact_name`/`contact_linkedin` (same tombstone filter — see
   * `loadContactDisplay` in `queue.ts`). Null when the contact has no title
   * on file, is absent from the mirror, or is tombstoned. The queue row
   * renders this on its own line between the name and the account — it is
   * NOT part of `contactDisplayIdentity`'s fallback chain, which is
   * unaffected by this field.
   */
  contact_title: z.string().nullable(),
  account_id: z.string().nullable(),
  /**
   * The account's `salesforce_account.name`, joined in at read time by this
   * item's own `account_id` (U1) — NOT derived by walking contact -> account.
   * Null when `account_id` is null, the account isn't in the mirror, or the
   * item predates account-id snapshotting. The row simply omits the
   * secondary account line when this is null.
   */
  account_name: z.string().nullable(),
  play_type_id: z.number().nullable(),
  play_instance_ref: z.string().nullable(),
  sender: z.string().nullable(),
  subject_draft: z.string().nullable(),
  body_draft: z.string().nullable(),
  note: z.string().nullable(),
  // Warm intro connector selection (Warm Intro plan U5/U8) — soft ref to
  // salesforce_warm_intro.id. Set at add-time (QueueAddSchema, below) or
  // later via ItemPatchSchema's connector picker (U8, not yet wired here).
  warm_intro_id: z.string().nullable(),
  // Warm intro "Preferred Channel" picklist (round-2 QA feedback, Task 4) —
  // 'Email' | 'Linkedin Message', enforced by ItemPatchSchema's write-side
  // enum (this read-side field stays a plain nullable string so an item
  // predating this column, or one whose value fell out of the two literals
  // via a hand-edit, still round-trips instead of failing the read parse).
  preferred_channel: z.string().nullable(),
  source_context: z.string().nullable(),
  /**
   * Gong Flow Play (U6/U10) — rep-set override to bypass Gong's send-time
   * cool-off/already-in-flow guard (core.prisma's `EnrollmentItem.
   * coolOffOverride` doc comment has the open question on exactly which
   * guard). DB-backed `NOT NULL DEFAULT false`
   * (`20260819000000_gong_flow_play_tables` migration), so every row has a
   * concrete value — `.default(false)` here is a parse-time safety net for
   * any caller that doesn't select the column, not a real fallback: every
   * live read path (`toWireItem`'s callers, `queue-send.ts`'s
   * `CLAIM_RETURNING`) selects the real column.
   */
  cool_off_override: z.boolean().default(false),
  /**
   * Flow Contacts Bulk Add-to-Queue Scope 2 (U2a) — the rep's explicit
   * "Send anyway" override for the `recent_invite` send-time gate (U2b,
   * `admitQueueSend`): a persisted, per-item boolean so the override
   * survives reload and reads consistently across both queue groupings.
   * DB-backed `NOT NULL DEFAULT false` (additive migration, no backfill),
   * so `.default(false)` here is the same parse-time safety net
   * `cool_off_override` uses above, not a real fallback — every live read
   * path selects the real column.
   *
   * Deliberately NOT a reuse of `cool_off_override`: that column is
   * `gong_flow`-specific payload config that rides the Gong send request
   * (`enrollment-sender.ts`), so overloading it here would silently flip
   * Gong's own downstream guard as a side effect of a Terra-local override.
   */
  recent_invite_override: z.boolean().default(false),
  /**
   * Flow Contacts Bulk Add-to-Queue Scope 2 (U2a) — whether this item's
   * contact has ANY OTHER `enrollment_item` submitted (any play, any owner)
   * within the last 30 days OR a recent `launcher_invitee_actions` 'invited'
   * row (Custom Invite) (`getRecentQueueSendContactIds`, `recent-sends.ts`).
   * Joined in at READ TIME, like `contact_name`/
   * `account_name`/`step_drafts` above — it is never an `enrollment_items`
   * column. `.default(false)` is the same parse-time safety net those
   * fields use: every live read path (`toWireItem`'s callers) passes the
   * real batch-computed value, so a caller that forgets to join simply
   * sees "not flagged" rather than a parse failure.
   */
  has_recent_queue_send: z.boolean().default(false),
  /**
   * Sparse per-step draft overrides for a `gong_flow` play (Gong Flow Play +
   * Queue redesign, U11) — joined in at read time from `enrollment_item_step`
   * (`toWireItem`'s callers batch-load these, never a per-item read; see
   * `loadStepDraftsBatch`/`listStepDrafts` in `step-drafts.ts`). `.default([])`
   * is the same parse-time safety net `cool_off_override` uses above: every
   * live read path passes the real (possibly empty) array, so a caller that
   * forgets to join simply gets an item with no visible overrides rather than
   * a parse failure. An item with no overridden steps returns `[]`, never
   * `null`/`undefined`.
   */
  step_drafts: z.array(StepDraftSchema).default([]),
  status: EnrollmentItemStatusSchema,
  last_send_error: z.string().nullable(),
  /**
   * Reaper's stuck-in-`sending` marker (PR #1312 R3) — ISO timestamp of when
   * the row was flagged, null when it isn't flagged. Set ONLY by the reaper
   * (never by a rep action), and nulled by the send-completion/`failItem`
   * paths in the SAME write that moves the row out of `sending`
   * (`queue-send.ts`), so a recovered item never wears a stale label.
   *
   * A flagged item still reads `status: "sending"` — the flag is deliberately
   * NOT a status value, because the system cannot know whether the
   * destination committed the send (Decision 3). Consumers surface it the way
   * they surface `last_send_error`: an inline warning on the item, never a
   * status pill.
   *
   * Required-and-nullable (no `.default()`, unlike `cool_off_override` /
   * `step_drafts` above): every producer sends it — `toWireItem`'s
   * `EnrollmentItemRow` requires the column and no read path narrows it away,
   * and `queue-send.ts`'s `CLAIM_RETURNING` selects it explicitly — so an
   * absent field means a producer regressed, and failing the parse loudly is
   * the correct outcome rather than silently reporting "not flagged".
   */
  flagged_stale_at: z.string().nullable(),
  correlation_id: z.string().nullable(),
  execution_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
}).strict();
export type EnrollmentItem = z.infer<typeof EnrollmentItemSchema>;

/**
 * enrollment_batch row — deliberately thin (D2: one active batch per
 * owner, implicit get-or-create, no batch CRUD). Play grouping in the UI
 * is derived from items, not stored here.
 */
export const EnrollmentBatchSchema = z.object({
  id: z.number(),
  owner_user_id: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
}).strict();
export type EnrollmentBatch = z.infer<typeof EnrollmentBatchSchema>;

// ── Request bodies (/api/flow/queue) ─────────────────────────────────────

/**
 * One add-time step override (Gong Flow Play + Queue redesign, U11, spec §7
 * rule 5) — the "Suggested" one-click add's pre-filled per-step copy, landed
 * in the SAME request as the item create (never a follow-up PUT round trip;
 * same "one request, not add-then-PATCH" philosophy as `warm_intro_id`
 * above). `.strict()` so an unrecognized key 400s here rather than being
 * silently dropped by `upsertStepDraft`.
 */
export const QueueAddStepOverrideSchema = z.object({
  step_number: z.number().int().positive(),
  step_type: z.string(),
  subject_draft: z.string().nullable().optional(),
  body_draft: z.string().nullable().optional(),
}).strict();
export type QueueAddStepOverride = z.infer<typeof QueueAddStepOverrideSchema>;

/**
 * POST /api/flow/queue/items — add a contact to the caller's active batch.
 *
 * `play_type_id`/`play_instance_ref` are OPTIONAL (2026-07-23 Spencer
 * deep-link decisions, #3 — "Suggested" invite card one-click add-with-play):
 * mirrors ItemPatchSchema's field shapes so the same both-or-neither
 * validation (`assertPlayFieldsPaired`, src/services/flow/queue.ts) covers
 * both the add and patch paths without duplicating the rule.
 */
export const QueueAddSchema = z.object({
  contactId: z.string(),
  sourceContext: z.string().optional(),
  note: z.string().optional(),
  play_type_id: z.number().nullable().optional(),
  play_instance_ref: z.string().nullable().optional(),
  // Warm intro connector preselect (Warm Intro plan Key Decision 6): the
  // pill's add-time intent passes the chosen connector's
  // salesforce_warm_intro.id so it lands on the item in the same request
  // — no follow-up PATCH (rejected: racy, two round-trips). NOT nullable
  // here — add-time preselect only ever supplies a value or omits it.
  warm_intro_id: z.string().optional(),
  // Gong Flow Play pre-fill (U11) — the "Suggested" invite card's per-step
  // copy overrides, applied via `upsertStepDraft` in the SAME add request.
  // `.max(50)` mirrors `SendSchema`'s array-safety convention (M3): a
  // no-cap array is an unbounded write loop off one request, and no real
  // Gong flow has anywhere near 50 steps.
  step_overrides: z.array(QueueAddStepOverrideSchema).max(50).optional(),
}).strict();
export type QueueAdd = z.infer<typeof QueueAddSchema>;

/**
 * PUT /api/flow/queue/items/:id/steps/:n — the per-step draft editor's
 * autosave body (Gong Flow Play + Queue redesign, U11). `step_type` is
 * required on every call (not just the first) — the row is denormalized
 * (`EnrollmentItemStep.stepType` doc comment) so a later partial PATCH still
 * states what kind of step this is. `subject_draft`/`body_draft` are each
 * independently optional so a subject-only or body-only autosave preserves
 * whichever field it omits (`upsertStepDraft`'s partial-upsert semantics) —
 * omit the key to leave it untouched, `null` to clear it.
 */
export const StepDraftWriteSchema = z.object({
  step_type: z.string(),
  subject_draft: z.string().nullable().optional(),
  body_draft: z.string().nullable().optional(),
}).strict();
export type StepDraftWrite = z.infer<typeof StepDraftWriteSchema>;

/**
 * PATCH /api/flow/queue/items/:id — any subset of play assignment, draft
 * edits, or a status transition. `status` is intentionally narrower than
 * `EnrollmentItemStatusSchema`: API-driven transitions are limited to
 * "ready"/"draft" — "submitted"/"sending" are set only by the send path
 * (D1), never by this patch endpoint.
 *
 * The two-play-fields-move-together invariant (M6: both set or both null,
 * else GET /api/flow/queue 500s on the grouping refine) is NOT a schema
 * refine here — a partial PATCH may legitimately carry just one field while
 * the other stays at its stored value, so both-or-neither can only be judged
 * against the effective post-patch pair. It's enforced in `patchQueueItem`
 * (InvalidPatchError → 400).
 */
export const ItemPatchSchema = z.object({
  play_type_id: z.number().nullable().optional(),
  play_instance_ref: z.string().nullable().optional(),
  sender: z.string().nullable().optional(),
  subject_draft: z.string().nullable().optional(),
  body_draft: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  // The connector picker's selection (Warm Intro plan U8). Nullable — unlike
  // QueueAddSchema's add-time preselect — because the editor can also CLEAR
  // it. A non-null value is checked against `salesforce_warm_intro` in
  // `patchQueueItem` (it must be a row for THIS item's contact): a schema
  // can't see the item, so the reference rule lives at that trust boundary,
  // not here.
  warm_intro_id: z.string().nullable().optional(),
  // The Preferred Channel picker's selection (round-2 QA feedback, Task 4).
  // A strict two-literal enum (not the read-side schema's plain string) —
  // an out-of-enum value is a 400 at this Zod boundary before `patchQueueItem`
  // ever sees it. `null` clears back to "use the computed default", same
  // clearing semantics as `warm_intro_id`.
  preferred_channel: z.enum(['Email', 'Linkedin Message']).nullable().optional(),
  // Gong Flow Play (U6/U10/U11) — the rep's send-time guard bypass. Not
  // nullable: the DB column is `NOT NULL DEFAULT false` (no "unset" state to
  // clear back to), so the only two writable values are `true`/`false`;
  // omitting the key (not `null`) is how a patch leaves it untouched.
  cool_off_override: z.boolean().optional(),
  // Flow Contacts Bulk Add-to-Queue Scope 2 (U2a) — the rep's explicit
  // "Send anyway" for the `recent_invite` send-time gate. Not nullable: the
  // DB column is `NOT NULL DEFAULT false` (no "unset" state to clear back
  // to), so the only two writable values are `true`/`false`; omitting the
  // key (not `null`) is how a patch leaves it untouched — the exact
  // `cool_off_override` convention above.
  recent_invite_override: z.boolean().optional(),
  status: z.enum(['ready', 'draft']).optional(),
}).strict();
export type ItemPatch = z.infer<typeof ItemPatchSchema>;

/**
 * POST /api/flow/queue/items/bulk — enroll many contacts into ONE play in a
 * single request (flow-contacts-bulk-send plan, U2; the Contacts table's
 * "Add N to <play>" popover).
 *
 * Strictly play-scoped (decision 15): both play fields are REQUIRED and
 * non-nullable, unlike `QueueAddSchema`'s optional pair. Bulk exists to land
 * a review-ready batch for a specific play — it never creates an unassigned
 * item, so there is no "Unassigned" bulk option and no both-or-neither
 * ambiguity for `assertPlayFieldsPaired` to adjudicate.
 *
 * `.max(100)` mirrors `SendSchema`'s cap (M3 array-safety convention, and the
 * interim flat cap per the plan's decision E — per-action-type caps are a
 * named follow-up). `.min(1)` rejects an empty batch with 400 rather than
 * returning an empty report.
 */
export const BulkAddSchema = z.object({
  contactIds: z.array(z.string().min(1)).min(1).max(100),
  play_type_id: z.number().int(),
  play_instance_ref: z.string().min(1),
  // Same provenance tag the single add carries (`QueueAddSchema.sourceContext`)
  // — the bulk popover states which surface the batch came from.
  sourceContext: z.string().optional(),
}).strict();
export type BulkAdd = z.infer<typeof BulkAddSchema>;

/**
 * What happened to ONE requested contact in a bulk add.
 *
 * - `added` — a row now exists for this contact on the requested play: either
 *   freshly created, or an existing UNASSIGNED open item adopted onto the play
 *   (the same compare-and-set the single-contact `WorkflowsDropdown` enroll
 *   uses — `maybeAssignPlayToUnassigned`, src/services/flow/queue.ts).
 * - `skipped_already_queued` — the contact already holds an open item, on ANY
 *   play. A contact may appear in the active queue only once per owner (the
 *   `(owner_user_id, contact_id) WHERE status <> 'submitted'` partial unique
 *   index), and bulk never re-points another play's in-progress item at this
 *   play (plan Risk 4). Also the outcome for the SECOND appearance of a
 *   duplicated id inside one request.
 * - `skipped_no_email` — the play requires a contact email (derived from its
 *   connector `inputSchema`, never a hardcoded slug) and this contact has none.
 *
 * The vocabulary is deliberately closed at these three: a requested contact id
 * that doesn't resolve to a LIVE `salesforce_contact` row is a 400 for the
 * whole request (an invalid reference in an otherwise schema-valid body, the
 * same contract the single add's unknown-contact 400 states), validated before
 * any row is written — not a per-contact skip.
 */
export const BulkAddOutcomeSchema = z.enum(['added', 'skipped_already_queued', 'skipped_no_email']);
export type BulkAddOutcome = z.infer<typeof BulkAddOutcomeSchema>;

/**
 * One per-contact row of the bulk report. `item_id` is present whenever the
 * outcome resolved to an actual `enrollment_items` row — the created/adopted
 * item for `added`, the pre-existing open item for `skipped_already_queued` —
 * and absent for `skipped_no_email` (nothing was written).
 */
export const BulkAddResultSchema = z.object({
  contact_id: z.string(),
  outcome: BulkAddOutcomeSchema,
  item_id: z.number().optional(),
}).strict();
export type BulkAddResult = z.infer<typeof BulkAddResultSchema>;

/**
 * POST /api/flow/queue/items/bulk response — 200 (a mixed-outcome REPORT, not
 * a pure create like the single add's 201: a request can legitimately land
 * zero rows and still be a success).
 *
 * `results` is index-aligned with the request's `contactIds`, duplicates
 * included, so the caller can attribute an outcome to each row it selected
 * without re-deriving order. Counts for the confirmation banner ("N added, M
 * skipped") are derived from this array — deliberately not a separate
 * server-computed field that could disagree with it.
 */
export const BulkAddResponseSchema = z.object({
  results: z.array(BulkAddResultSchema),
}).strict();
export type BulkAddResponse = z.infer<typeof BulkAddResponseSchema>;

/**
 * The rep's browser-local staged base copy/sender snapshot (DBOS
 * durable-send migration, F1 U2, Decision 6) — rides the existing
 * `SendSchema` body instead of a new "save staged config" endpoint (R10):
 * nothing here is ever persisted server-side except as the resolved
 * per-item trio the claim writes. Keys of `base_copy`/`base_sender` are
 * `playConfigKey` strings (`${playTypeId}:${playInstanceRef}` —
 * `web/app-home/src/components/flow/EnrollQueue/state/staged-play-config.ts:117`);
 * the server recomputes that key from each row and looks it up here — it
 * NEVER trusts a client-supplied key to identify which row it belongs to.
 * `.refine` caps each record at 50 entries (a rep's queue review batch is
 * nowhere near that large; mirrors `SendSchema.itemIds`'s own `.max(100)`
 * array-safety convention, M3) — a 51st entry is a 400 at this schema
 * boundary, never a 500 or a silent truncation.
 */
export const StagedSendConfigSchema = z.object({
  base_copy: z.record(z.string(), z.object({
    subject: z.string(),
    body: z.string(),
  }).strict()).refine(v => Object.keys(v).length <= 50, { message: 'base_copy must not exceed 50 entries' }),
  base_sender: z.record(z.string(), z.string())
    .refine(v => Object.keys(v).length <= 50, { message: 'base_sender must not exceed 50 entries' }),
  // .max(500): a rep hand-picking a sender per row inside one queue batch —
  // generous relative to SendSchema's own 100-item cap since senderTouched
  // can outlive a single send (it persists across a browsing session).
  sender_touched: z.array(z.number().int()).max(500),
}).strict();
export type StagedSendConfig = z.infer<typeof StagedSendConfigSchema>;

/**
 * POST /api/flow/queue/send — one request shape for both single-item and
 * batch sends (D7). `.min(1)` rejects an empty batch with 400 rather than
 * silently admitting nothing.
 */
export const SendSchema = z.object({
  // .int(): a float like 1.5 must be a 400, not a silent 'not_ready' skip
  // after findMany matches nothing.
  // .max(100): cap the batch (M3) — an unbounded array is hours of detached
  // serialized sends, and each duplicate POST is another overlapping loop.
  itemIds: z.array(z.number().int()).min(1).max(100),
  // Optional (F1 U2) — old clients omit it entirely, which must behave
  // identically to today's row-only resolution (Behavior 5/3).
  staged: StagedSendConfigSchema.optional(),
}).strict();
export type Send = z.infer<typeof SendSchema>;

/**
 * A requested item that was NOT admitted into the send queue — either it
 * doesn't exist / isn't owned by the caller (`not_found`) or it failed a
 * specific eligibility check `admitQueueSend` now runs itself (U3 follow-up
 * — the extended vocabulary below), so a rep sees exactly WHY an item didn't
 * send instead of a generic `not_ready`.
 *
 * `not_ready`/`not_found` are KEPT (never removed — an in-flight client can
 * still receive them; `terra/no-parallel-schema-and-type` widening rule):
 * `not_found` for an unknown/not-owned id, `not_ready` for a tombstoned
 * contact or any other generic non-eligible state that doesn't map to one of
 * the specific reasons below. The new members mirror `assessSendEligibility`'s
 * (`src/services/flow/queue.ts`) return values plus two claim-time states:
 * `no_play` (no play assigned), `no_sender` (blank effective sender),
 * `no_email` (email-requiring play, no contact email), `no_connector`
 * (warm_intro with no connector selected), `already_sent` (persisted
 * successful-send correlation + `submitted` status — re-submission), and
 * `sending` (another concurrent request already claimed the row — B1).
 */
export const SendSkipSchema = z.object({
  item_id: z.number(),
  reason: z.enum([
    'not_ready',
    'not_found',
    'no_play',
    'no_sender',
    'no_email',
    'no_connector',
    'already_sent',
    'sending',
    // Flow Contacts Bulk Add-to-Queue Scope 2 (U2b) — the contact was sent
    // ANY play from the queue within the last 30 days and this item has no
    // `recent_invite_override`. Claim-time-only (see `assessSendEligibility`'s
    // doc comment) — members are only ever added, never removed.
    'recent_invite',
  ]),
}).strict();
export type SendSkip = z.infer<typeof SendSkipSchema>;

/**
 * POST /api/flow/queue/send response — 202 (assertion 7: admission result
 * only, never the sender's result). `accepted` ids were admitted into the
 * send queue; their `correlation_id`/`execution_id`/`status: "submitted"`
 * land later, off this request, once `EnrollmentSender` resolves.
 */
export const SendResponseSchema = z.object({
  accepted: z.array(z.number()),
  skipped: z.array(SendSkipSchema),
}).strict();
export type SendResponse = z.infer<typeof SendResponseSchema>;

/**
 * POST /api/flow/queue/items/bulk-delete — remove up to 100 items from the
 * queue in ONE request (flow-contacts-bulk-send QA round-3 plan, D21 — the
 * By Play card's "Clear all N"). Structural precedent is `SendSchema` above
 * (per-id outcomes, `.max(100)` array-safety cap, M3), not `BulkAddSchema`
 * — this is an item-id operation, not a contact-id one, so there is no play
 * or contact payload to carry alongside the ids.
 */
export const BulkDeleteSchema = z.object({
  itemIds: z.array(z.number().int()).min(1).max(100),
}).strict();
export type BulkDelete = z.infer<typeof BulkDeleteSchema>;

/**
 * What happened to ONE requested item id in a bulk delete.
 *
 * - `removed` — the row existed, was owned by the caller, and was not
 *   `sending` — it is gone.
 * - `skipped_not_found` — the id doesn't exist, OR it exists but is owned by
 *   a DIFFERENT caller. Deliberately indistinguishable (never a 403 for one
 *   id inside an otherwise-valid batch) — the same "not-owned is
 *   indistinguishable from missing" contract `deleteQueueItem`
 *   (`src/services/flow/queue.ts`) already relies on for the single-item
 *   DELETE's 404.
 * - `skipped_sending` — the row exists, is owned by the caller, but is
 *   currently `sending` (an in-flight send claimed it — deleting it now
 *   would race the send/write-back and could double-enroll the contact,
 *   the same guard `deleteQueueItem`'s own doc comment explains).
 */
export const BulkDeleteOutcomeSchema = z.enum(['removed', 'skipped_not_found', 'skipped_sending']);
export type BulkDeleteOutcome = z.infer<typeof BulkDeleteOutcomeSchema>;

/** One per-item row of the bulk-delete report. */
export const BulkDeleteResultSchema = z.object({
  item_id: z.number(),
  outcome: BulkDeleteOutcomeSchema,
}).strict();
export type BulkDeleteResult = z.infer<typeof BulkDeleteResultSchema>;

/**
 * POST /api/flow/queue/items/bulk-delete response — 200 (a mixed-outcome
 * REPORT, same convention as `BulkAddResponseSchema`: a request can
 * legitimately remove zero rows — every requested id already `sending` —
 * and still be a success). `results` has one row per UNIQUE requested id
 * (deduped, first-occurrence order) — never a second row for a duplicated
 * id, since deleting the same id twice is a no-op the second time and a
 * caller counting "N removed" from this array must never double-count one
 * row.
 */
export const BulkDeleteResponseSchema = z.object({
  results: z.array(BulkDeleteResultSchema),
}).strict();
export type BulkDeleteResponse = z.infer<typeof BulkDeleteResponseSchema>;

// ── Response schemas ─────────────────────────────────────────────────────

/**
 * D7 (flow-contacts-bulk-send design-fidelity plan, item 7) — the ONE
 * fallback `play_name` string every producer AND consumer uses when a
 * play's instance can't be resolved (deleted/no-longer-exposed Gong flow,
 * unregistered play type, etc.). Used server-side by
 * `groupQueueItems`/`resolveGroupPlayInfo` (`src/services/flow/queue.ts`)
 * — the ONLY place a `play_name` should ever originate degraded — and
 * client-side by `QueueBody.tsx`'s `resolveGroupName` and
 * `EnrollQueueContext.tsx`'s send-toast snapshot, both of which only need
 * this for defensive type-narrowing (the producer always sets `play_name`
 * for a real assigned group) or the toast's own `item.play_instance_ref`-only
 * fallback path. This used to be a raw `` `Play ${instanceRef}` `` template,
 * which put a raw Salesforce campaign id (`701…`) in front of a rep as
 * visible copy — a raw id must never be visible copy, tooltip or no.
 */
export const UNRESOLVED_PLAY_NAME = 'Play unavailable';

/**
 * Per-group play display + capability fields (U4, F4 — catalog payload
 * trim). Added so clients no longer need to scan the full play catalog
 * (`GET /api/flow/queue/plays`'s `plays` array) to find a group's
 * name/slug/capabilities — the catalog no longer carries per-instance rows
 * for a `catalog: true` exposure kind (today: `gong_flow`, ~660 rows), so
 * that scan would silently come up empty for exactly the groups that most
 * need it. `play_type_slug`/`has_editable_copy`/`requires_email`/
 * `copy_required`/`ui` reuse `PlayOptionSchema`'s own field schemas (never
 * hand-duplicated — `terra/no-duplicate-schema-name`), the same
 * shared-schema-reuse convention `ActivePlayTypeSchema`
 * (`src/shared/flow/play-catalog.ts`) follows via `.pick()`. All nullable,
 * matching `play_type_id`/`play_instance_ref` above: the unassigned group
 * (`play_type_id: null`) carries none of these, and `null` for
 * `play_type_slug`/`has_editable_copy`/`requires_email`/`copy_required`/`ui`
 * additionally covers the (should-be-unreachable) case
 * where an assigned group's play type itself no longer resolves. `play_name`
 * degrades independently — an assigned group whose play TYPE still resolves
 * but whose specific INSTANCE no longer does (e.g. a deleted Gong flow)
 * keeps its type-level slug/capabilities/ui and falls back only on
 * `UNRESOLVED_PLAY_NAME` (matching the client's existing `resolveGroupName`
 * miss convention — `QueueBody.tsx`).
 */
const QueueGroupSchema = z.object({
  play_type_id: z.number().nullable(),
  play_instance_ref: z.string().nullable(),
  play_name: z.string().nullable(),
  play_type_slug: PlayOptionSchema.shape.play_type_slug.nullable(),
  has_editable_copy: PlayOptionSchema.shape.has_editable_copy.nullable(),
  requires_email: PlayOptionSchema.shape.requires_email.nullable(),
  copy_required: PlayOptionSchema.shape.copy_required.nullable(),
  ui: PlayUiCapabilitiesSchema.nullable(),
  items: z.array(EnrollmentItemSchema),
});

/**
 * GET /api/flow/queue — items grouped by play. The unassigned group
 * (play_type_id: null) always sorts first (UI: the unassigned lane leads).
 */
export const QueueResponseSchema = z.object({
  groups: z.array(QueueGroupSchema),
}).superRefine((val, ctx) => {
  const unassignedIndex = val.groups.findIndex(g => g.play_type_id === null);
  if (unassignedIndex > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'unassigned group (play_type_id: null) must sort first',
      path: ['groups', unassignedIndex],
    });
  }
});
export type QueueResponse = z.infer<typeof QueueResponseSchema>;

/**
 * GET /api/flow/queue/items/:id/warnings — anti-double-flow (D3), sourced
 * from already-synced signals (Gong fields, Playhouse invited-recently
 * store). A discriminated union on `kind` — not a plain `{code, message}`
 * bag — so the response is signal-source-agnostic (D3): each variant lists
 * only the fields that specific badge needs, and `.strict()` on every
 * branch keeps a source's internal details (e.g. `gong_flow_name`, table
 * names) from ever leaking into the wire contract. Non-blocking by
 * construction — there is no field here that could gate Queue/Send.
 */
export const InFlowWarningSchema = z.object({
  kind: z.literal('in_flow'),
  flowName: z.string().nullable(),
  flowStatus: z.string().nullable(),
  /**
   * U27 (Gong reconcile D14): where the answer came from. `gong` = the
   * `gong_flow_probe` (fresh); `salesforce` = the synced
   * `salesforce_contact.gong_in_flow` flag, used only when the contact has no
   * probe row. Optional so older producers/consumers keep parsing.
   */
  source: z.enum(['gong', 'salesforce']).optional(),
  /** Label suffix shown with a Salesforce-sourced answer ("may lag"). */
  sourceNote: z.string().optional(),
  /** The Signal Action owner on the live Gong enrollment (probe-sourced only). */
  ownerEmail: z.string().nullable().optional(),
}).strict();

export const RecentInviteWarningSchema = z.object({
  kind: z.literal('recent_invite'),
}).strict();

/**
 * mirror-delete-propagation (codex adversarial-review Finding 2, bead .5
 * follow-up): the contact backing this item was deleted/merged in
 * Salesforce. `/warnings` is informational-only (never gates Queue/Send),
 * so this degrades in place of the 500 the endpoint used to throw — it does
 * not suppress whatever OTHER warnings still independently apply (e.g.
 * `in_flow`, which reads a field already present on the tombstoned row).
 */
export const ContactDeletedWarningSchema = z.object({
  kind: z.literal('contact_deleted'),
}).strict();

export const WarningSchema = z.discriminatedUnion('kind', [
  ContactDeletedWarningSchema,
  InFlowWarningSchema,
  RecentInviteWarningSchema,
]);
export type Warning = z.infer<typeof WarningSchema>;

/**
 * GET /api/flow/queue/items/:id/warnings response — a bare JSON array (not
 * `{ warnings: [...] }`), in badge-render order; a clean contact returns
 * `[]`, never `null`/`undefined`.
 */
export const WarningsResponseSchema = z.array(WarningSchema);
export type WarningsResponse = z.infer<typeof WarningsResponseSchema>;

// GET /api/flow/queue/senders — the per-lead sender picker

/**
 * One sender option. Server-owned list, single-sited in
 * `listSenderOptions` (src/services/flow/sender-resolution.ts) — the exact
 * Playhouse sender picker (CLAY_TEAM_SENDERS + owner/claydr/I-Will-Send)
 * plus 'self'. `value` is the SenderSelection wire value stored on
 * enrollment_item.sender and validated at send time by resolveSender.
 */
export const SenderOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
}).strict();
export type SenderOption = z.infer<typeof SenderOptionSchema>;

export const SendersResponseSchema = z.object({
  senders: z.array(SenderOptionSchema),
  /**
   * The role-derived sender this play type would have SEEDED for the acting
   * rep (`defaultSenderForPlay`, src/services/flow/play-senders.ts) —
   * design-fidelity plan D12 point 3, the only backend change in that unit
   * and read-only.
   *
   * Exists because the frontend cannot compute it: the default is a function
   * of the rep's `User.userType`, which no wire payload carries. The
   * play-level Sender field (`BaseCopyEditor`) shows it as the field's clean
   * value, so a rep sees a concrete sender instead of a "Select sender…"
   * placeholder.
   *
   * `null` means "this play type has no default for this rep" — today only
   * event_invite for a userType with no positive Flow role (MEMBER/REVOPS);
   * the field then genuinely shows its placeholder. Always one of `senders`'
   * `value`s when non-null, never a resolved email address.
   *
   * `.optional()` (Codex adversarial finding, D12 follow-up): a `.strict()`
   * object still REQUIRES a `.nullable()` key to be present, just allows its
   * value to be `null` — so any caller/mock built against the pre-D12 shape
   * (`{ senders: [...] }`, no `default_sender` key at all) fails validation
   * outright instead of degrading. `.optional()` restores backward
   * compatibility for that old shape. Read sites must normalize
   * `undefined` to `null` themselves — this schema deliberately does not
   * paper over that with a Zod default, so `string | null | undefined`
   * stays visible in the inferred type at every call site.
   */
  default_sender: z.string().nullable().optional(),
}).strict();
export type SendersResponse = z.infer<typeof SendersResponseSchema>;

// GET /api/flow/queue/items/:id/input-options — per-ITEM field options

/**
 * Options for ONE input field whose `ActionInputField.optionsSource` says the
 * server resolves them per item (Warm Intro plan U8). `key` is the input
 * schema's field key (`'Warm Intro Id'` for the connector picker,
 * `'Preferred Channel'` for the Task-4 picklist — round-2 QA feedback), and
 * the option `value` is what a PATCH persists.
 *
 * `persistsTo` (round-2 QA feedback, Task 4) is the discriminator the CLIENT
 * switches on to know which `ItemPatch` column an option's `value` writes
 * into — added because a second `optionsSource` kind landed alongside the
 * connector picker, and the client must not switch on the field's `key`
 * string to decide that (that literal is owned by the play type's
 * definition file server-side; mirroring it client-side would be drift
 * waiting to happen — see EnrollQueueContext.tsx's `selectInputOption` doc
 * comment, which called this out before the second kind existed).
 *
 * `subject`/`body` (round-2 QA feedback, Task 3) ride on the connector
 * picker's OPTIONS ONLY (never on Preferred Channel's) — the rendered
 * Subject/Body prefill varies PER CONNECTOR (the sign-off name), so it can't
 * be a single flat field on the response; it has to travel with the option
 * the rep is choosing between. Optional: every other options field simply
 * omits them.
 *
 * Deliberately NOT `SenderOptionSchema` reused: senders are a play-TYPE list
 * (same for every item of that type), these are per-CONTACT and can change
 * under the rep between two items of the same play. Similar {value,label}
 * shape, different contract — a shared schema would tie two independent wire
 * surfaces together.
 */
export const InputFieldOptionsSchema = z.object({
  key: z.string(),
  persistsTo: z.enum(['warm_intro_id', 'preferred_channel']),
  options: z.array(z.object({
    value: z.string(),
    label: z.string(),
    subject: z.string().optional(),
    body: z.string().optional(),
  }).strict()),
}).strict();
export type InputFieldOptions = z.infer<typeof InputFieldOptionsSchema>;

/**
 * GET /api/flow/queue/items/:id/input-options response. Keyed BY FIELD (not
 * a bare option list) so a second `optionsSource` kind — or a play type with
 * two server-resolved pickers — is an additive entry rather than a breaking
 * reshape. Items whose play type declares no `optionsSource` field (every
 * play type but warm_intro today) return `{ fields: [] }`.
 */
export const InputOptionsResponseSchema = z.object({
  fields: z.array(InputFieldOptionsSchema),
}).strict();
export type InputOptionsResponse = z.infer<typeof InputOptionsResponseSchema>;

// GET /api/flow/queue/items/:id/execution — auto-open-on-send

/**
 * GET /api/flow/queue/items/:id/execution response. The send response
 * (`SendResponseSchema` above) is admission-only — `execution_id` lands on
 * the row asynchronously, off the request path, once `FlowSendWorkflow`'s
 * `finalizeItem` step writes it (src/services/flow/flow-send-steps.ts) on
 * `terra-worker`. Once an item leaves the
 * active queue (`GET /api/flow/queue` filters `submitted` rows out), this
 * is the client's only way to learn its execution id — the read that backs
 * "mark ready -> send -> auto-open the created request" (single-item send).
 *
 * `status` reuses the Requests feed's `RequestStatusSchema` (mapped from the
 * linked `ActionExecution`'s `deliveryStatus`, same mapper the feed itself
 * uses) rather than the raw `EnrollmentItemStatus` — the client uses it to
 * seed an accurate optimistic `RequestEntry` (see `RequestEntrySchema`) for
 * the sidebar's Requests-tab detail view, so a synchronous sender
 * (event_invite, already `delivered` at send time) doesn't optimistically
 * flash a "still executing" state it will immediately have to correct.
 */
export const QueueItemExecutionResponseSchema = z.object({
  execution_id: z.string().nullable(),
  status: RequestStatusSchema,
}).strict();
export type QueueItemExecutionResponse = z.infer<typeof QueueItemExecutionResponseSchema>;
